import { MarketingHero } from '../../../domain/marketing-hero';
import {
  CreateLocalizedStringApiModel,
  CreateMarketingHeroPageItemApiModel,
  LocalizedStringApiModel,
  MarketingHeroPageItemApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  buttonMapperDomainToApi,
  createLocalizedText,
  descriptionLocalizedMapperDomainToApi,
  localizedBackgroundImagesMapper,
  mapDomainImageDataToApi,
  platformTargetingMapper,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import { TrayWithLocalizedHeadlineText } from '../../../domain/tray-common';
import {
  buttonMapperApiToDomain,
  commonMapper,
  descriptionLocalizedMapperApiToDomain,
  mapApiModelImageWithPlatformToDomain,
  scheduledDateMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { MarketingHeroTrayFormViewModel } from './marketing-hero-tray-form-view-model';
import { SchedulingType } from '../../../shared/enums/scheduling-type.enum';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import { mapLocalizedDomainImagesToPlatformLocalizedViewModelImage } from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  mapMarketingButtonToDomainButton,
  mapViewModelImagesToLocalizedDomainImages,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'MarketingHero'

/**
 * DOMAIN -> API
 */

const headlineLocalizedFromHeadlineMapper = <T extends TrayWithLocalizedHeadlineText>(
  tray: T
): { headLineLocalized: CreateLocalizedStringApiModel } => {
  return {
    headLineLocalized: createLocalizedText(tray.headlineTextEnglish, tray.headlineTextArabic),
  };
};

export const mapDomainMarketingHeroToApi = (
  marketingHero: MarketingHero,
  pageVersionId: string,
  tenantId?: string
): CreateMarketingHeroPageItemApiModel => {
  return remainderMapper(
    mapperPipe(marketingHero, targetingMapperDomainToApi, scheduledDateMapperDomainToApi, buttonMapperDomainToApi, platformTargetingMapper),
    () => ({
      pageVersionId,
      slug: pageVersionId,
      name: marketingHero.titleEnglish,
      tenantId,
      messages: marketingHero.messages.map((message) => {
        return remainderMapper(
          mapperPipe(message, headlineLocalizedFromHeadlineMapper, descriptionLocalizedMapperDomainToApi, localizedBackgroundImagesMapper),
          () => ({
            images: message.backgroundImages.en.map(mapDomainImageDataToApi),
          })
        );
      }),
    })
  );
};

/**
 * API -> DOMAIN
 */

const headlineTextFromHeadlineLocalizedMapper = <T extends { headLineLocalized: LocalizedStringApiModel }>(
  tray: T
): TrayWithLocalizedHeadlineText => {
  return {
    headlineTextArabic: tray.headLineLocalized.ar,
    headlineTextEnglish: tray.headLineLocalized.en,
  };
};

export const mapApiMarketingHeroToDomain = (hero: MarketingHeroPageItemApiModel): MarketingHero => {
  return remainderMapper(
    mapperPipe(hero, commonMapper, targetingMapperDomainToApi, buttonMapperApiToDomain, scheduledDateMapperApiToDomain),
    () => ({
      type: 'MarketingHero',
      titleEnglish: hero.name,
      messages: hero.messages.map((message) => {
        return remainderMapper(mapperPipe(message, headlineTextFromHeadlineLocalizedMapper, descriptionLocalizedMapperApiToDomain), () => ({
          backgroundImages: {
            en: message.images.map(mapApiModelImageWithPlatformToDomain),
          },
        }));
      }),
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapDomainMarketingHeroToViewModel = (hero: MarketingHero): MarketingHeroTrayFormViewModel => {
  return {
    id: hero.id,
    button: {
      action: hero.primaryButton.action,
      buttonTextEnglish: hero.primaryButton.buttonTextEnglish,
      buttonTextArabic: hero.primaryButton.buttonTextArabic,
      actionPageTargetId: hero.primaryButton.buttonActionPageTargetId,
      actionExternalUrl: hero.primaryButton.buttonActionExternalUrl,
      actionProgramTarget: hero.primaryButton.buttonActionProgramTarget
        ? {
            availabilityFrom: hero.primaryButton.buttonActionProgramTarget.availabilityFrom,
            availabilityTo: hero.primaryButton.buttonActionProgramTarget.availabilityTo,
            id: hero.primaryButton.buttonActionProgramTarget.id,
            title: hero.primaryButton.buttonActionProgramTarget.title,
            episode: hero.primaryButton.buttonActionProgramTarget.episode,
            season: hero.primaryButton.buttonActionProgramTarget.season,
          }
        : undefined,
    },
    scheduling: {
      schedulingType: hero.scheduledFrom || hero.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
      scheduledFrom: hero.scheduledFrom ? new Date(hero.scheduledFrom).toISOString() : undefined,
      scheduledTo: hero.scheduledTo ? new Date(hero.scheduledTo).toISOString() : undefined,
    },
    type: TrayFormViewModelType.MarketingHero,
    userSegmentIds: hero.userSegmentIds,
    countryIds: hero.countryIds,
    name: hero.titleEnglish,
    messages: hero.messages.map((message) => ({
      headlineText: { en: message.headlineTextEnglish, ar: message.headlineTextArabic },
      description: { en: message.descriptionTextEnglish, ar: message.descriptionTextArabic },
      backgroundImages: mapLocalizedDomainImagesToPlatformLocalizedViewModelImage(message.backgroundImages),
    })),
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapViewModelMarketingHeroToDomain = (
  hero: MarketingHeroTrayFormViewModel,
  platformIds: Record<PlatformType, string>
): MarketingHero => {
  return {
    id: hero.id,
    type: 'MarketingHero',
    titleEnglish: hero.name,
    order: undefined,
    visible: undefined,
    draggable: true,
    userSegmentIds: hero.userSegmentIds,
    countryIds: hero.countryIds,
    scheduledFrom: hero.scheduling?.scheduledFrom ? new Date(hero.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: hero.scheduling?.scheduledTo ? new Date(hero.scheduling.scheduledTo).getTime() : undefined,
    primaryButton: mapMarketingButtonToDomainButton(hero.button),
    messages: hero.messages.map((message) => ({
      descriptionTextArabic: message.description.ar,
      descriptionTextEnglish: message.description.en,
      headlineTextEnglish: message.headlineText.en,
      headlineTextArabic: message.headlineText.ar,
      backgroundImages: mapViewModelImagesToLocalizedDomainImages(message.backgroundImages, platformIds),
    })),
    platformIds: Object.values(platformIds),
  };
};
